import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { SimpleApprovalDialog } from "@/features/documents/ApprovalBanner";
import { CancelDialog } from "@/features/documents/CancelDialog";
import { DocumentDetail } from "@/features/documents/DocumentDetail";
import { DocumentEditor } from "@/features/documents/DocumentEditor";
import { PaymentDialog } from "@/features/documents/PaymentDialog";
import { ReversePaymentDialog } from "@/features/documents/ReversePaymentDialog";
import { SendDialog } from "@/features/documents/SendDialog";
import { documentKey } from "@/features/documents/useDocument";
import { get } from "@/lib/api";
import type { Approval, DocumentDetail as Detail } from "@/lib/types";

/** `?freigabe=<id>`: the agent's request opens the dialog its action belongs to, in approval mode. */
function FreigabeDialog({ detail, id, onClose, onBooked }: { detail: Detail; id: number; onClose: () => void; onBooked: () => void }) {
  const navigate = useNavigate();
  const listed = detail.approvals.find((a) => a.id === id);
  // A link to a request of another document (or one the list no longer carries) is fetched by itself.
  const fetched = useQuery({ queryKey: ["approval", id], queryFn: () => get<Approval>(`/api/approvals/${id}`), enabled: !listed });
  const approval = listed ?? fetched.data;
  const elsewhere = approval && approval.documentId !== detail.document.id ? approval : null;
  useEffect(() => {
    if (fetched.isError) {
      toast.error(fetched.error.message);
      onClose();
    }
  }, [fetched.isError, fetched.error, onClose]);
  useEffect(() => {
    if (elsewhere) navigate(`/rechnungen/${elsewhere.documentId}?freigabe=${elsewhere.id}`, { replace: true });
  }, [elsewhere, navigate]);
  if (!approval || elsewhere) return null;
  const number = detail.document.number ?? detail.nextNumber;
  switch (approval.action) {
    case "send":
    case "finalize":
      return <SendDialog detail={detail} approval={approval} onClose={onClose} />;
    case "reminder":
      return <SendDialog detail={detail} kind="reminder" approval={approval} onClose={onClose} />;
    case "payment":
      return <PaymentDialog detail={detail} approval={approval} onClose={onClose} onBooked={onBooked} />;
    case "payment_reverse":
      return <ReversePaymentDialog detail={detail} paymentId={Number(approval.args.paymentId)} approval={approval} onClose={onClose} />;
    case "cancel":
      return <CancelDialog detail={detail} approval={approval} onClose={onClose} />;
    case "delete_draft":
      return (
        <SimpleApprovalDialog
          approval={approval}
          number={number}
          onClose={onClose}
          onDone={() => {
            toast.success("Entwurf gelöscht");
            navigate(detail.document.type === "offer" ? "/rechnungen?art=angebote" : "/rechnungen?stufe=draft");
          }}
        />
      );
    case "threshold_override":
      return <SimpleApprovalDialog approval={approval} number={number} onClose={onClose} onDone={() => { toast.success("Freigegeben"); onClose(); }} />;
  }
}

/** `/rechnungen/:id`: a draft opens in the editor, a finalized document in its detail view. */
export function Dokument() {
  const id = Number(useParams().id);
  const [sp, setSp] = useSearchParams();
  const freigabe = Number(sp.get("freigabe"));
  const [flashFrom, setFlashFrom] = useState<number | null>(null);
  // A flash belongs to one document: going to another must not mark its rows as new.
  useEffect(() => setFlashFrom(null), [id]);
  const closeFreigabe = useCallback(() => setSp((prev) => {
    const next = new URLSearchParams(prev);
    next.delete("freigabe");
    return next;
  }, { replace: true }), [setSp]);
  const detail = useQuery({
    queryKey: documentKey(id),
    queryFn: () => get<Detail>(`/api/documents/${id}`),
    enabled: Number.isInteger(id) && id > 0,
  });

  if (detail.isError) {
    return (
      <div className="space-y-3">
        <PageHeader title="Dokument" parent={{ to: "/rechnungen", label: "Rechnungen" }} />
        <p className="text-sm text-destructive">{detail.error.message}</p>
      </div>
    );
  }
  if (!detail.data) return <div aria-busy className="h-40 animate-pulse rounded-xl bg-muted motion-reduce:animate-none" />;

  const data = detail.data;
  const doc = data.document;
  const onBooked = () => setFlashFrom(Math.max(0, ...data.payments.map((p) => p.id)));

  return (
    <>
      {doc.status === "draft" && doc.finalizedAt === null ? (
        <DocumentEditor key={`doc-${doc.id}`} detail={data} />
      ) : (
        <DocumentDetail key={`doc-${doc.id}`} detail={data} flashFrom={flashFrom} onBooked={onBooked} />
      )}
      {Number.isInteger(freigabe) && freigabe > 0 && (
        <FreigabeDialog key={`freigabe-${freigabe}`} detail={data} id={freigabe} onClose={closeFreigabe} onBooked={onBooked} />
      )}
    </>
  );
}
